var _a;
export let nanoid = (t = 21) => Transport.crypto
    .getRandomValues(new Uint8Array(t))
    .reduce((t, e) => (t += (e &= 63) < 36 ? e.toString(36) : e < 62 ? (e - 26).toString(36).toUpperCase() : e > 62 ? '-' : '_'), '');
const nodeVer = typeof process !== 'undefined' && ((_a = process.versions) === null || _a === void 0 ? void 0 : _a.node);
export async function getCrypto() {
    if (typeof crypto != 'undefined')
        return crypto;
    try {
        const packageName = 'node:crypto';
        var { webcrypto } = require(packageName);
        return webcrypto;
    }
    catch (e) {
        try {
            const { webcrypto } = await import(/* @vite-ignore */ /* webpackIgnore: true */ 'node'.substring(0) + ':crypto');
            return webcrypto;
        }
        catch (e) {
            console.log('Crypto is not found. Update to node that has node:crypto ', e);
        }
    }
    return crypto;
}
export async function getFetch() {
    if (typeof fetch != 'undefined') {
        return fetch;
    }
    try {
        const packageName = 'node-fetch';
        return require(packageName);
    }
    catch (e) {
        try {
            const { default: nodeFetch } = await import(
            /* @vite-ignore */ /* webpackIgnore: true */ 'node'.substring(0) + '-fetch');
            return nodeFetch;
        }
        catch (e) {
            throw new Error('fetch() is not available. Install `node-fetch` or update node to v17+');
        }
    }
}
export class HTTPError extends Error {
    constructor(message, details) {
        super(message, details);
        this.status = details.status;
    }
}
export class Transport {
    constructor(options) {
        this.nanoid = nanoid;
        this.parseObjectDates = (object) => {
            if (object == null || typeof object != 'object') {
                return object;
            }
            if (Array.isArray(object)) {
                return object.map(this.parseObjectDates, this);
            }
            var result = {};
            for (var property in object) {
                if (property.endsWith('At') && object[property] != null) {
                    result[property] = new Date(object[property]);
                }
                else {
                    result[property] = this.parseObjectDates(object[property]);
                }
            }
            return result;
        };
        Object.assign(this, Transport.defaults);
        Object.assign(this, options);
    }
    async checkFetchResponse(response) {
        var _a;
        if (!response.ok) {
            const isJsonResponse = (_a = response.headers.get('content-type')) === null || _a === void 0 ? void 0 : _a.includes('application/json');
            if (isJsonResponse) {
                const json = await response.json();
                throw new HTTPError(json.message, { status: response.status });
            }
            else {
                throw new HTTPError(`Request failed with ${response.status} status`, { status: response.status });
            }
        }
        return response;
    }
    log(...args) {
        if (this.verbose) {
            console.log(...args);
        }
    }
    async fetch(url, options = {}) {
        var _a, _b, _c;
        if (this.preflight)
            options = await this.preflight(url, options);
        const absoluteUrl = url.startsWith('/') ? this.backend + url : url;
        this.log(((_a = options.method) === null || _a === void 0 ? void 0 : _a.toUpperCase()) || 'GET', ' ' + absoluteUrl);
        const apiKeyHeader = this.apiKey || ((_b = this.library) === null || _b === void 0 ? void 0 : _b.apiKey) ? { 'x-api-key': this.apiKey || ((_c = this.library) === null || _c === void 0 ? void 0 : _c.apiKey) } : {};
        const authorizationHeader = this.accessToken ? { Authorization: `Bearer ${this.accessToken}` } : {};
        const initOptions = {
            ...options,
            headers: {
                'Content-Type': 'application/json',
                ...authorizationHeader,
                ...apiKeyHeader,
                ...options.headers
            }
        };
        const fetch = Transport.fetch || (await Transport.fetchPromise);
        return fetch(absoluteUrl, initOptions).then(this.checkFetchResponse);
    }
    async fetchJSON(url, options = {}) {
        return this.fetch(url, options)
            .then((res) => {
            return res.json();
        })
            .then(this.parseObjectDates)
            .then((object) => {
            if (object) {
                if (Array.isArray(object)) {
                    this.log('  > ', object.length, ' items');
                }
                else {
                    this.log('  > #', object['id'], object['version'] ? '(v' + object['revision'] + ')' : '');
                }
            }
            return object;
        });
    }
    async fetchExternalJSON(url, options = {}) {
        if (this.useProxy) {
            return this.proxyJSON(url, options);
        }
        else {
            return this.fetchJSON(url, options);
        }
    }
    async proxyJSON(url, options) {
        return this.fetchJSON(`/proxy/fetch`, {
            method: 'POST',
            body: JSON.stringify({ url, options })
        });
    }
    async proxy(url, options) {
        return this.fetch(`/proxy/fetch`, {
            method: 'POST',
            body: JSON.stringify({ url, options })
        });
    }
    async signUpload(category) {
        return this.fetchJSON(`/files/generateSAS`, {
            method: 'POST',
            body: JSON.stringify({ category })
        });
    }
    async uploadBlob(category, name, blob, options = {}) {
        const { url } = await this.signUpload(category);
        const domain = url.split('?')[0];
        const blobUrl = `${domain}/${category}/${name}`;
        const uploadUrl = url.replace('?', `/${category}/${name}?`);
        const fetch = Transport.fetch || (await Transport.fetchPromise);
        return await fetch(uploadUrl, {
            method: 'PUT',
            body: blob,
            ...options,
            headers: {
                accept: 'application/xml',
                'x-ms-blob-type': 'BlockBlob',
                'x-ms-revision': '2021-08-06',
                ...options.headers
            }
        }).then(() => {
            return {
                url: blobUrl
            };
        });
        //return Transport.fetch(url)
    }
    reportError(e, message) {
        var _a;
        (_a = this.onError) === null || _a === void 0 ? void 0 : _a.call(this, e, message);
    }
}
Transport.nanoid = nanoid;
Transport.defaults = {
    cdn: `https://feaas.blob.core.windows.net`,
    backend: 'https://components.sitecorecloud.io/api',
    frontend: 'https://components.sitecorecloud.io',
    formsBackend: 'https://forms-entities-{region}.sitecorecloud.io',
    inventoryEnv: 'production',
    sitecorePostfix: 'sitecorecloud.io',
    inventory: 'https://platform-inventory.sitecorecloud.io'
};
if (typeof crypto != 'undefined')
    Transport.crypto = crypto;
if (typeof fetch != 'undefined')
    Transport.fetch = fetch;
Transport.fetchPromise = getFetch().then((fetch) => (Transport.fetch = fetch));
Transport.cryptoPromise = getCrypto().then((crypto) => (Transport.crypto = crypto));
export default Transport;
//# sourceMappingURL=transport.js.map